import { observer } from 'mobx-react-lite'
import { Box, IconButton, Typography } from 'shared/ui'
import { useShortcutTooltip } from 'shared/ui/ShortcutTooltip/useShortcutTooltip'
import { useChips } from 'shared/hooks/useChips.ts'
import { EnhancePrompt } from 'entities/AIAgent/actions/enhancePrompt/EnhancePrompt'
import { ActionTemplateMap } from 'entities/AIAgent/lib/agentActions.ts'
import type { IAgentHandoffNotifySettings } from 'entities/Chatbot'
import { MessageField } from 'widgets/MessageField'
import { handoffToHuman } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/handoffToHuman/handoffToHuman.tsx'
import { ShortcutMenu } from './ui/ShortcutMenu/ShortcutMenu'
import { type InstructionsSectionStore } from './store/instructionsSectionStore'

export type IInstructionsSectionContentProps = {
  store: InstructionsSectionStore
  isExpanded?: boolean
  onOpenModal?: () => void
}

export const InstructionsSectionContent = observer(
  ({ store, isExpanded, onOpenModal }: IInstructionsSectionContentProps) => {
    const {
      messageFieldStore,
      instructions,
      error,
      setError,
      setInstructions,
      requestEnhance,
      mergeFieldConfig,
      onAddMergeField,
      onActionItemClick,
      onAddHandoffToHumanAction,
      onUpdateHandoffToHumanAction,
      deleteAction,
    } = store
    const { replaceTriggerContent, updateTriggerContent, textFieldElement } = messageFieldStore

    const { triggerTooltip, infos } = useShortcutTooltip({
      textFieldElement,
      actions: [
        {
          shortcut: '/',
          infoLabel: 'to add contact fields and actions',
          renderComponent: (onClose) => (
            <ShortcutMenu
              onClose={onClose}
              mergeFieldConfig={mergeFieldConfig}
              onAddMergeField={onAddMergeField}
              onActionItemClick={onActionItemClick}
              onAddHandoffToHumanAction={onAddHandoffToHumanAction}
            />
          ),
        },
      ],
    })

    useChips({
      textFieldElement,
      replaceTriggerContent,
      updateTriggerContent,
      actions: {
        [ActionTemplateMap.addTag]: store.onActionChipClick,
        [ActionTemplateMap.removeTag]: store.onActionChipClick,
        [ActionTemplateMap.addNote]: store.onActionChipClick,
        [ActionTemplateMap.sendSMS]: store.onActionChipClick,
      },
    })

    return (
      <>
        {triggerTooltip}
        <Box
          display={'flex'}
          alignItems={'flex-start'}
          justifyContent={'space-between'}
          marginBottom={'24px'}
        >
          <Box display={'flex'} flexDirection={'column'}>
            <Typography
              variant={isExpanded ? 'heading-lg' : 'body-lg-medium'}
              ariaLabel={'Instructions'}
            >
              Instructions
            </Typography>
            {infos}
          </Box>
          <Box display={'flex'} alignItems={'center'} gap={'2px'}>
            <EnhancePrompt
              prompt={instructions}
              onAcceptPrompt={setInstructions}
              setError={setError}
              requestEnhance={requestEnhance}
            />
            {!isExpanded && (
              <IconButton
                iconProps={{ fontSize: 16 }}
                variant={'icon'}
                color={'tertiary'}
                size={'small'}
                icon={'solidExpand'}
                ariaLabel='Expand'
                onClick={onOpenModal}
                tooltipProps={{
                  label: 'Expand',
                  placement: 'top',
                }}
              />
            )}
          </Box>
        </Box>
        <MessageField messageFieldStore={messageFieldStore} uiStyle={'new'} error={error} />
      </>
    )
  }
)
